import React, { useState, useEffect } from 'react';
import { reviewService } from '../../../services/api';
import { Review, ReviewStats } from '../../../types/review';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import PhotoModal from '../../../components/ui/PhotoModal';
import { useCafeteriaSelection } from '../../../context/CafeteriaSelectionContext';

interface ReviewsPageProps {
  placeId?: string; // Tornar opcional para compatibilidade
}

const ReviewsPage: React.FC<ReviewsPageProps> = ({ placeId: propPlaceId }) => {
  const { currentCafeteria } = useCafeteriaSelection();
  const placeId = propPlaceId || currentCafeteria?.id || '';

  // Se não há cafeteria selecionada, mostrar mensagem
  if (!currentCafeteria) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-coffee-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <div className="w-12 h-12 bg-coffee-200 rounded-full"></div>
          </div>
          <div className="h-8 bg-coffee-200 rounded-lg w-48 mx-auto mb-4 animate-pulse"></div>
          <div className="h-6 bg-coffee-200 rounded-lg w-64 mx-auto animate-pulse"></div>
        </div>
      </div>
    );
  }

  const [reviews, setReviews] = useState<Review[]>([]);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [respondingTo, setRespondingTo] = useState<string | null>(null);
  const [responseText, setResponseText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [photoModalIndex, setPhotoModalIndex] = useState(0);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

  const ITEMS_PER_PAGE = 10;

  // Carregar estatísticas
  const loadStats = async () => {
    try {
      setLoadingStats(true);
      setStatsError(null);
      const data = await reviewService.getReviewStats(placeId);
      
      // O endpoint dashboard/stats retorna uma estrutura diferente
      const transformedStats: ReviewStats = {
        totalReviews: data.totalReviews || 0,
        averageRating: data.averageRating || 0,
        ratingDistribution: data.ratingDistribution || {},
        recentReviews: [] // Não há recentReviews no endpoint de stats
      };
      
      setStats(transformedStats);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      setStatsError('Erro ao carregar estatísticas. Tente novamente.');
    } finally {
      setLoadingStats(false);
    }
  };

  // Carregar avaliações
  const loadReviews = async (reset = false) => {
    try {
      setLoading(true);
      setError(null);
      const page = reset ? 0 : currentPage;
      const skip = page * ITEMS_PER_PAGE;
      
      const data = await reviewService.getReviews(placeId, {
        skip,
        take: ITEMS_PER_PAGE,
      });

      // O endpoint recent-reviews retorna diretamente um array de reviews
      const reviewsArray = Array.isArray(data) ? data : data.reviews || [];
      
      // Debug: log da estrutura dos dados retornados
      console.log('Reviews data structure:', {
        dataType: Array.isArray(data) ? 'array' : typeof data,
        dataLength: Array.isArray(data) ? data.length : 'N/A',
        firstReview: reviewsArray[0] ? {
          id: reviewsArray[0].id,
          hasPhotoUrls: !!reviewsArray[0].photoUrls,
          hasPhotos: !!reviewsArray[0].photos,
          hasImages: !!reviewsArray[0].images,
          photoUrls: reviewsArray[0].photoUrls,
          photos: reviewsArray[0].photos,
          images: reviewsArray[0].images
        } : 'No reviews'
      });
      
      // Transformar dados da API para o formato esperado pelo componente
      const transformedReviews = reviewsArray.map((review: any) => {
        // Extrair URLs das fotos de diferentes possíveis estruturas da API
        let photoUrls: string[] = [];
        
        if (review.photoUrls && Array.isArray(review.photoUrls)) {
          photoUrls = review.photoUrls;
        } else if (review.photos && Array.isArray(review.photos)) {
          photoUrls = review.photos.map((photo: any) => 
            typeof photo === 'string' ? photo : photo.url || photo.photoUrl
          ).filter(Boolean);
        } else if (review.images && Array.isArray(review.images)) {
          photoUrls = review.images.map((image: any) => 
            typeof image === 'string' ? image : image.url || image.imageUrl
          ).filter(Boolean);
        }

        return {
          id: review.id,
          rating: review.rating,
          comment: review.comment || review.text || '', // Mapear 'comment' ou 'text'
          createdAt: review.createdAt,
          user: {
            id: review.userId || review.user?.id || 'unknown',
            fullName: review.userName || review.user?.fullName || review.user?.name || 'Usuário Anônimo',
            email: review.user?.email || ''
          },
          place: {
            id: placeId,
            name: review.place?.name || ''
          },
          photoUrls: photoUrls,
          photos: review.photos || []
        };
      });

      if (reset) {
        setReviews(transformedReviews);
        setCurrentPage(0);
      } else {
        setReviews(prev => [...prev, ...transformedReviews]);
      }
      
      // Verificar se há mais páginas baseado no número de itens retornados
      setHasMore(reviewsArray.length === ITEMS_PER_PAGE);
    } catch (error) {
      console.error('Erro ao carregar avaliações:', error);
      setError('Erro ao carregar avaliações. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Carregar mais avaliações
  const loadMore = () => {
    if (!loading && hasMore) {
      setCurrentPage(prev => prev + 1);
      loadReviews(false);
    }
  };

  // Abrir modal de fotos
  const handlePhotoClick = (photos: string[], index: number) => {
    setSelectedPhotos(photos);
    setPhotoModalIndex(index);
    setIsPhotoModalOpen(true);
  };

  // Fechar modal de fotos
  const handleClosePhotoModal = () => {
    setIsPhotoModalOpen(false);
    setSelectedPhotos([]);
    setPhotoModalIndex(0);
  };

  // Responder a uma avaliação
  const handleRespond = async (reviewId: string) => {
    if (!responseText.trim()) return;
    
    try {
      await reviewService.respondToReview(placeId, reviewId, responseText);
      setResponseText('');
      setRespondingTo(null);
      // Recarregar avaliações para mostrar a resposta
      loadReviews(true);
      // Mostrar feedback de sucesso
      alert('Resposta enviada com sucesso!');
    } catch (error) {
      console.error('Erro ao responder avaliação:', error);
      alert('Erro ao enviar resposta. Tente novamente.');
    }
  };

  // Formatar data
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 24) {
      return `há ${diffInHours} horas`;
    } else if (diffInHours < 48) {
      return 'há 1 dia';
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `há ${diffInDays} dias`;
    }
  };

  // Renderizar estrelas
  const renderStars = (rating: number) => {
    return '⭐'.repeat(rating);
  };

  useEffect(() => {
    loadStats();
    loadReviews(true);
  }, [placeId]);

  if (loading && reviews.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-coffee-800">Avaliações</h1>
          <p className="text-coffee-600 mt-2">
            
          </p>
        </div>
        <div className="flex space-x-3">
          <div className="text-sm text-coffee-600">
            Total: {reviews.length} avaliações
          </div>
        </div>
      </div>

      {/* Error Messages */}
      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button 
            onClick={() => loadReviews(true)}
            className="btn btn-sm btn-outline"
          >
            Tentar Novamente
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-coffee-800">
                Lista de Avaliações ({reviews.length})
              </h3>
            </div>
            <div className="card-body">
              {reviews.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-coffee-600">
                    Nenhuma avaliação encontrada.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {reviews.map((review) => (
                    <div key={review.id} className="border-b border-coffee-100 pb-6 last:border-b-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="w-10 h-10 bg-coffee-200 rounded-full flex items-center justify-center">
                              <span className="text-coffee-800 font-medium">
                                {review.user.fullName.split(' ').map(n => n[0]).join('').toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-coffee-800">{review.user.fullName}</p>
                              <div className="flex items-center space-x-2">
                                <span className="text-yellow-400">{renderStars(review.rating)}</span>
                                <span className="text-sm text-coffee-600">{formatDate(review.createdAt)}</span>
                              </div>
                            </div>
                          </div>
                          
                          {review.comment && (
                            <p className="text-coffee-700 mb-3">"{review.comment}"</p>
                          )}

                          {/* Exibir fotos da review */}
                          {(review.photoUrls && review.photoUrls.length > 0) && (
                            <div className="flex space-x-2 mb-3">
                              {review.photoUrls.slice(0, 3).map((photoUrl, index) => (
                                <img
                                  key={index}
                                  src={photoUrl}
                                  alt={`Review photo ${index + 1}`}
                                  className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={() => handlePhotoClick(review.photoUrls, index)}
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.src = '/placeholder-image.png';
                                  }}
                                />
                              ))}
                              {review.photoUrls.length > 3 && (
                                <div 
                                  className="w-16 h-16 bg-coffee-200 rounded flex items-center justify-center cursor-pointer hover:bg-coffee-300 transition-colors"
                                  onClick={() => handlePhotoClick(review.photoUrls, 3)}
                                >
                                  <span className="text-coffee-600 text-sm">+{review.photoUrls.length - 3}</span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Área de resposta */}
                          {respondingTo === review.id && (
                            <div className="mt-3 p-3 bg-coffee-50 rounded">
                              <textarea
                                value={responseText}
                                onChange={(e) => setResponseText(e.target.value)}
                                placeholder="Digite sua resposta..."
                                className="w-full p-2 border border-coffee-200 rounded resize-none"
                                rows={3}
                              />
                              <div className="flex space-x-2 mt-2">
                                <button
                                  onClick={() => handleRespond(review.id)}
                                  className="btn btn-success btn-sm"
                                  disabled={!responseText.trim()}
                                >
                                  Enviar Resposta
                                </button>
                                <button
                                  onClick={() => {
                                    setRespondingTo(null);
                                    setResponseText('');
                                  }}
                                  className="btn btn-secondary btn-sm"
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* <div className="flex space-x-2">
                          <button
                            onClick={() => setRespondingTo(review.id)}
                            className="btn btn-success btn-sm"
                            disabled={respondingTo === review.id}
                          >
                            Responder
                          </button>
                          <div className="dropdown dropdown-end">
                            <button 
                              className="btn btn-warning btn-sm"
                              disabled={moderating === review.id}
                            >
                              {moderating === review.id ? <LoadingSpinner size="sm" /> : 'Moderar'}
                            </button>
                          </div>
                        </div> */}
                      </div>
                    </div>
                  ))}

                  {hasMore && (
                    <div className="text-center pt-4">
                      <button
                        onClick={loadMore}
                        disabled={loading}
                        className="btn btn-outline btn-coffee"
                      >
                        {loading ? <LoadingSpinner size="sm" /> : 'Carregar mais'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Estatísticas */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-coffee-800">
                Estatísticas
              </h3>
            </div>
            <div className="card-body">
              {loadingStats ? (
                <LoadingSpinner />
              ) : statsError ? (
                <div className="text-center">
                  <p className="text-error text-sm mb-2">{statsError}</p>
                  <button 
                    onClick={loadStats}
                    className="btn btn-sm btn-outline"
                  >
                    Tentar Novamente
                  </button>
                </div>
              ) : stats ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-coffee-600">Total de Avaliações</p>
                    <p className="text-2xl font-bold text-coffee-800">{stats.totalReviews}</p>
                  </div>
                  <div>
                    <p className="text-sm text-coffee-600">Avaliação Média</p>
                    <p className="text-2xl font-bold text-coffee-800">{stats.averageRating?.toFixed(2)} ⭐</p>
                  </div>
                </div>
              ) : (
                <p className="text-coffee-600">Erro ao carregar estatísticas</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de fotos */}
      <PhotoModal
        photos={selectedPhotos}
        currentIndex={photoModalIndex}
        isOpen={isPhotoModalOpen}
        onClose={handleClosePhotoModal}
      />
    </div>
  );
};

export default ReviewsPage; 